import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { Checkbox, FormField, Input } from '@ds/forms';
import { SegmentedControl } from '@ds/navigation';
import { dataSimples, diaIso, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { ConteudoDoDocumento } from '@/comuns/documentoLegal';
import { DOCUMENTOS } from '@/data/juridico';
import { campoDeCartao } from '@/data/stripeNoNavegador';
import { VERSOES_DOS_TERMOS, autocadastro } from '@/session/autocadastro';
import type { CampoDeCartao } from '@/data/stripeNoNavegador';
import type { PlanoDoCadastro } from '@/session/autocadastro';

type Forma = 'cartao' | 'pix';

interface PixGerado {
    copiaECola: string | null;
    imagemBase64: string | null;
    expiraEm: string | null;
    valorCentavos: number;
}

const DIAS_DE_TESTE = 14;
const CONFERIR_PIX_A_CADA_MS = 5000;

function somarDias(dia: string, dias: number): string {
    const d = new Date(`${dia}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + dias);
    return d.toISOString().slice(0, 10);
}

const estiloDoLink = {
    border: 0, padding: 0, background: 'transparent', cursor: 'pointer', font: 'inherit',
    color: 'var(--text-brand)', fontWeight: 600,
} as const;

/**
 * Passo 4: o pagamento.
 *
 * Cartão: 14 dias grátis, pela Stripe. **O número do cartão não passa por esta tela:**
 * os campos são um iframe da Stripe (Stripe.js, carregado do domínio dela com a chave
 * publicável que a borda entrega), e o que sai daqui é só um `pm_…`. Nem o estado do
 * React vê o número — é isso que mantém a instalação fora do escopo PCI mais pesado.
 * A assinatura nasce com 14 dias de teste: nada é cobrado hoje.
 *
 * Pix Automático: o QR paga o 1º mês e autoriza o débito mensal no banco. A clínica
 * abre quando o aviso do banco chega; a tela confere sozinha a cada 5 segundos, e
 * "Já paguei" confere na hora.
 */
export function EtapaPagamento({ pendenteId, plano, aoConcluir, aoVoltar }: {
    pendenteId: string;
    plano: PlanoDoCadastro | null;
    aoConcluir: () => void | Promise<void>;
    aoVoltar: () => void;
}) {
    const aviso = useAviso();
    const [forma, setForma] = useState<Forma>('cartao');
    const [titular, setTitular] = useState('');
    const [cep, setCep] = useState('');
    const [aceite, setAceite] = useState(false);
    const [documentoAberto, setDocumentoAberto] = useState<'termos' | 'privacidade' | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [enviando, setEnviando] = useState(false);
    const [pix, setPix] = useState<PixGerado | null>(null);
    const [aguardando, setAguardando] = useState<string | null>(null);
    const [campo, setCampo] = useState<CampoDeCartao | null>(null);
    const [preparando, setPreparando] = useState(true);
    const [erroDoCartao, setErroDoCartao] = useState<string | null>(null);
    const caixaDoCartao = useRef<HTMLDivElement | null>(null);

    const ids = { titular: useId(), cep: useId() };
    const testeAte = somarDias(diaIso(), DIAS_DE_TESTE);

    /* O campo do cartão é um iframe da Stripe, e ele só pode ser montado depois de a
       borda dizer qual é a chave publicável desta instalação. Enquanto isso a tela mostra
       o lugar dele, para não pular o layout quando chegar. */
    useEffect(() => {
        if (forma !== 'cartao') return undefined;
        let vivo = true;
        let preparado: CampoDeCartao | null = null;
        void (async () => {
            setPreparando(true);
            const p = await autocadastro().preparoDoCartao();
            if (!vivo) return;
            if (!p.ok) {
                setErro(p.erro);
                setPreparando(false);
                return;
            }
            try {
                const c = await campoDeCartao(p.chavePublicavel);
                /* A tela pode ter saído enquanto o script carregava: montar agora deixaria
                   um iframe órfão no documento. */
                if (!vivo) {
                    c.desmontar();
                    return;
                }
                preparado = c;
                if (caixaDoCartao.current) c.montar(caixaDoCartao.current);
                c.aoMudar(({ erro: recusa }) => setErroDoCartao(recusa));
                setCampo(c);
            } catch (e) {
                setErro(e instanceof Error ? e.message : 'Não conseguimos carregar o formulário de cartão.');
            } finally {
                if (vivo) setPreparando(false);
            }
        })();
        return () => {
            vivo = false;
            preparado?.desmontar();
            setCampo(null);
        };
    }, [forma]);

    const exigirAceite = () => {
        if (aceite) return true;
        setErro('Aceite os Termos de uso e a Política de privacidade para continuar.');
        return false;
    };

    const pagarNoCartao = async () => {
        setErro(null);
        if (!exigirAceite()) return;
        if (!campo) {
            setErro('O formulário de cartão ainda está carregando. Tente de novo em um instante.');
            return;
        }
        setEnviando(true);
        /* Primeiro o cartão vira `pm_…` NO NAVEGADOR; só depois a borda é chamada. Cartão
           recusado nesta etapa nem chega ao servidor — e a mensagem da Stripe já vem em
           português, escrita para quem está pagando. */
        const preparado = await campo.tokenizar({ nome: titular, ...(cep.trim() ? { cep: cep.replace(/\D/g, '') } : {}) });
        if (!preparado.ok) {
            setEnviando(false);
            setErro(preparado.erro);
            return;
        }
        const r = await autocadastro().pagarNoCartao({
            pendente: pendenteId,
            meioDePagamento: preparado.meioDePagamento,
            ...VERSOES_DOS_TERMOS,
        });
        setEnviando(false);
        if (!r.ok) {
            setErro(r.erro);
            return;
        }
        await aoConcluir();
    };

    const gerarPix = async () => {
        setErro(null);
        if (!exigirAceite()) return;
        setEnviando(true);
        const r = await autocadastro().gerarPix({ pendente: pendenteId, ...VERSOES_DOS_TERMOS });
        setEnviando(false);
        if (!r.ok) {
            setErro(r.erro);
            return;
        }
        setPix({ copiaECola: r.copiaECola, imagemBase64: r.imagemBase64, expiraEm: r.expiraEm, valorCentavos: r.valorCentavos });
    };

    /* Verdadeiro quando não há mais o que esperar: a clínica abriu, ou o Pix foi recusado. */
    const conferirPix = useCallback(async (): Promise<boolean> => {
        const s = await autocadastro().situacao();
        if (s.temClinica) {
            await aoConcluir();
            return true;
        }
        if (s.pendente?.erro) {
            setPix(null);
            setErro(s.pendente.erro);
            return true;
        }
        return false;
    }, [aoConcluir]);

    useEffect(() => {
        if (!pix) return undefined;
        const relogio = window.setInterval(() => {
            void conferirPix().then((acabou) => { if (acabou) window.clearInterval(relogio); }).catch(() => undefined);
        }, CONFERIR_PIX_A_CADA_MS);
        return () => window.clearInterval(relogio);
    }, [pix, conferirPix]);

    const jaPaguei = async () => {
        setAguardando(null);
        if (!(await conferirPix())) {
            setAguardando('Ainda não recebemos a confirmação do Pix. Ela pode levar alguns segundos depois do pagamento.');
        }
    };

    const copiar = async (texto: string) => {
        try {
            await navigator.clipboard.writeText(texto);
            aviso({ title: 'Código do Pix copiado', tone: 'success' });
        } catch {
            aviso({ title: 'Não foi possível copiar', description: 'Selecione o código e copie à mão.', tone: 'danger' });
        }
    };

    const resumo = (linhas: [string, string, boolean?][]) => (
        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px 12px', fontSize: 13, borderTop: '1px dashed var(--border-strong)', paddingTop: 10 }}>
            {linhas.map(([rotulo, valor, forte]) => (
                <div key={rotulo} style={{ display: 'contents' }}>
                    <dt style={{ color: forte ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: forte ? 600 : 400 }}>{rotulo}</dt>
                    <dd style={{ margin: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)', fontWeight: forte ? 600 : 400 }}>{valor}</dd>
                </div>
            ))}
        </dl>
    );

    const preco = plano ? moeda(plano.precoCentavos) : '—';

    const termos = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Checkbox checked={aceite} onChange={setAceite} label="Li e aceito os Termos de uso e a Política de privacidade" />
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                <button type="button" style={estiloDoLink} onClick={() => setDocumentoAberto('termos')}>Ler os Termos de uso</button>
                {' · '}
                <button type="button" style={estiloDoLink} onClick={() => setDocumentoAberto('privacidade')}>Ler a Política de privacidade</button>
            </p>
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600, color: 'var(--text-primary)' }}>Pagamento</h2>

            {!pix && (
                <SegmentedControl
                    ariaLabel="Forma de pagamento"
                    value={forma}
                    onChange={(v) => { setForma(v === 'pix' ? 'pix' : 'cartao'); setErro(null); }}
                    options={[{ value: 'cartao', label: 'Cartão · 14 dias grátis' }, { value: 'pix', label: 'Pix Automático' }]}
                />
            )}

            {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

            {forma === 'cartao' && (
                <form onSubmit={(e) => { e.preventDefault(); void pagarNoCartao(); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <FormField label="Nome impresso no cartão" htmlFor={ids.titular} required>
                        <Input id={ids.titular} name="titular" autoComplete="cc-name" value={titular} onChange={setTitular} />
                    </FormField>
                    <FormField
                        label="Cartão de crédito"
                        error={erroDoCartao}
                        hint={preparando ? 'Carregando o campo seguro do cartão…' : 'Número, validade e CVV. O cartão vai cifrado direto para a Stripe.'}
                        required
                    >
                        {/* O conteúdo é um iframe da Stripe: número, validade e CVV nunca
                            existem neste documento, e por isso não há `<Input>` aqui. */}
                        <div
                            ref={caixaDoCartao}
                            data-testid="campo-do-cartao"
                            style={{
                                minHeight: 36, padding: '9px 12px', border: '1px solid var(--border)',
                                borderRadius: 'var(--radius-md)', background: 'var(--surface)',
                            }}
                        />
                    </FormField>
                    <FormField label="CEP do titular" htmlFor={ids.cep} hint="Ajuda o banco a aprovar a cobrança." required>
                        <Input id={ids.cep} name="cep" autoComplete="postal-code" inputMode="numeric" value={cep} onChange={setCep} />
                    </FormField>

                    {resumo([['Hoje', moeda(0)], [dataSimples(testeAte), preco, true]])}
                    {termos}

                    <Button type="submit" fullWidth loading={enviando}>Começar teste grátis</Button>
                </form>
            )}

            {forma === 'pix' && !pix && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <p style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' }}>
                        Pague o 1º mês agora e autorize o débito mensal no aplicativo do seu banco. No Pix Automático não há teste grátis.
                    </p>
                    {resumo([['Hoje', preco, true], ['Todo mês', preco]])}
                    {termos}
                    <Button type="button" fullWidth loading={enviando} onClick={() => { void gerarPix(); }}>Gerar o QR do Pix</Button>
                </div>
            )}

            {pix && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
                    {pix.imagemBase64 && (
                        <img
                            src={`data:image/png;base64,${pix.imagemBase64}`}
                            alt="QR Code do Pix Automático"
                            width={188}
                            height={188}
                            style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: '#fff', padding: 6 }}
                        />
                    )}
                    <p style={{ margin: 0, fontSize: 13, textAlign: 'center', color: 'var(--text-secondary)' }}>
                        {moeda(pix.valorCentavos)} agora, e o mesmo valor todo mês.
                        {pix.expiraEm ? ` O QR vale até ${pix.expiraEm.slice(11, 16)}.` : ''}
                    </p>
                    {pix.copiaECola && (
                        <Button type="button" variant="secondary" fullWidth onClick={() => { void copiar(pix.copiaECola ?? ''); }}>Copiar código Pix</Button>
                    )}
                    {aguardando && <Alert tone="info" dismissible={false}>{aguardando}</Alert>}
                    <Button type="button" fullWidth onClick={() => { void jaPaguei(); }}>Já paguei</Button>
                </div>
            )}

            {!pix && <Button type="button" variant="secondary" onClick={aoVoltar}>Voltar</Button>}

            <Modal
                open={documentoAberto !== null}
                title={documentoAberto ? DOCUMENTOS[documentoAberto].titulo : undefined}
                description={documentoAberto ? `Versão ${DOCUMENTOS[documentoAberto].versao} · vigente desde ${DOCUMENTOS[documentoAberto].vigenteDesde}` : undefined}
                width={720}
                onClose={() => setDocumentoAberto(null)}
            >
                {documentoAberto && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <a href={DOCUMENTOS[documentoAberto].caminho} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-link)' }}>
                            Abrir em página inteira
                        </a>
                        <ConteudoDoDocumento id={documentoAberto} nivelDoTitulo={3} />
                    </div>
                )}
            </Modal>
        </div>
    );
}
